import { useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CustomButton } from "@/components/common/customButton";
import { CustomTextInput } from "@/components/common/inputFields/customTextInput";
import { ErrorDialog } from "@/components/common/errorDialog";
import { useErrorDialog } from "@/hooks/useErrorDialog";
import { askQuestion } from "@/services/marketReportApi/marketReportApi";
import type { AskResponse } from "@/types/marketReport";
import { CitationList } from "./components/CitationList";
import { AskVisualizationList } from "./components/AskVisualization";

export function AskTab() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<AskResponse | null>(null);
  const { errorDialog, showError, hideError } = useErrorDialog();

  const handleSubmit = async () => {
    const trimmed = question.trim();
    if (trimmed.length < 3) {
      showError(undefined, "Question must be at least 3 characters.");
      return;
    }
    if (trimmed.length > 4000) {
      showError(undefined, "Question must be 4000 characters or fewer.");
      return;
    }

    setLoading(true);
    setResponse(null);
    const result = await askQuestion(trimmed);
    setLoading(false);

    if (result.success && result.data) {
      setResponse(result.data);
    } else {
      showError(
        undefined,
        typeof result.message === "string"
          ? result.message
          : "Failed to get answer",
      );
    }
  };

  return (
    <Box className="market-reports-ask-tab">
      <Box className="market-reports-ask-form">
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 500, mb: 2 }}>
          Chat about market reports
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CustomTextInput
              label="Question"
              value={question}
              onChange={(value) => setQuestion(String(value))}
              multiline
            />
          </Box>
          <CustomButton
            text={loading ? "Chatting…" : "Chat"}
            handleButtonPress={() => void handleSubmit()}
            isButtonDisabled={loading || question.trim().length < 3}
            isLoading={loading}
          />
        </Box>
      </Box>

      <Box className="market-reports-ask-results">
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {response ? (
          <Box>
            {response.confidence != null ? (
              <Chip
                label={`Confidence: ${Math.round(response.confidence * 100)}%`}
                size="small"
                sx={{ mb: 1.5 }}
              />
            ) : null}

            {response.limitations ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {response.limitations}
              </Alert>
            ) : null}

            <div className="market-reports-ask-answer">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {response.answer}
              </ReactMarkdown>
            </div>

            <AskVisualizationList visualizations={response.visualizations} />
            <CitationList citations={response.citations} />
          </Box>
        ) : null}
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
}
